import { roundToPlaces } from "../../utils/roundToPlaces.js";
import type { CountrySettings } from "../CountrySettings.js";
import type { Coupon } from "../Coupon.js";
import type { Customer } from "../Customer.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { OrderInput } from "../Order.js";
import type { OrderPricesInput } from "../OrderPrices.js";
import type { Subcategory } from "../Subcategory.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculateOrderDiscounts } from "./calculateOrderDiscounts.js";
import { calculatePriceOfDelivery } from "./calculatePriceOfDelivery.js";
import { calculateRoundingAdjustment } from "./calculateRoundingAdjustment.js";
import { getPriceOfOrderProduct } from "./getPriceOfOrderProduct.js";
import { getWeightInGramsOfOrder } from "./getWeightInGramsOfOrder.js";
import { shouldApplyTax } from "./shouldApplyTax.js";

interface OrderPriceSettings {
	countrySettings: CountrySettings[];
	customer: Customer | null;
	exchangeRates: ExchangeRate[];
	subcategories: Pick<Subcategory, "id" | "eligibleForB2cDiscount" | "urlSlug">[];
	vatType: EconomicSubjectVatType;
	coupons: Coupon[];
}

export function calculatePriceOfOrder(
	order: Pick<
		OrderInput,
		"products" | "billingAddress" | "deliveryAddress" | "payment" | "deliveryType" | "couponCode"
	>,
	{ countrySettings, customer, exchangeRates, subcategories, vatType, coupons }: OrderPriceSettings,
): OrderPricesInput {
	let withoutTax = 0;
	// To conform with how Fakturoid works.
	// https://www.fakturoid.cz/podpora/statistiky/zpusob-vypoctu-dph#vypocet-dph-mozny-nesoulad
	let tax = 0;

	const applyTax = shouldApplyTax(order.deliveryAddress.country, vatType);

	for (const product of order.products) {
		const { withoutTax: productWithoutTax } = getPriceOfOrderProduct(product, { applyTax });
		withoutTax += productWithoutTax;
		tax += roundToPlaces(productWithoutTax * (product.info.price.taxRate / 100), 2);
	}

	let deliveryPrice: TaxedPrice = {
		taxRate: 0,
		withTax: 0,
		withoutTax: 0,
	};
	if (order.products.length > 0) {
		deliveryPrice = calculatePriceOfDelivery(order.deliveryAddress.country, order.deliveryType, {
			countrySettings,
			customer,
			weightInGrams: getWeightInGramsOfOrder(order),
			exchangeRates,
			currency: order.payment.currency,
			applyTax,
			vatType,
		});
	}

	const { freeDelivery, discountsByTaxRate } = calculateOrderDiscounts(order, {
		customer,
		countrySettings,
		subcategories,
		coupons,
	});

	const productDiscountWithoutTax = discountsByTaxRate.reduce((sum, { withoutTax }) => sum + withoutTax, 0);
	const productDiscountTax = discountsByTaxRate.reduce(
		(sum, { withoutTax, taxRate }) => sum + roundToPlaces(withoutTax * (taxRate / 100), 2),
		0,
	);
	const deliveryDiscountWithTax = freeDelivery ? deliveryPrice.withTax : 0;
	const deliveryDiscountWithoutTax = freeDelivery ? deliveryPrice.withoutTax : 0;

	withoutTax -= productDiscountWithoutTax;
	tax -= productDiscountTax;
	deliveryPrice.withTax -= deliveryDiscountWithTax;
	deliveryPrice.withoutTax -= deliveryDiscountWithoutTax;
	withoutTax += deliveryPrice.withoutTax;
	tax += roundToPlaces(deliveryPrice.withoutTax * (deliveryPrice.taxRate / 100), 2);

	if (!applyTax) {
		const totalWithoutTax = roundToPlaces(withoutTax, 2);
		const roundingAdjustment = calculateRoundingAdjustment(totalWithoutTax, order.payment.currency);
		return {
			// `calculatePriceOfDelivery` already accounts for "applyTax"
			deliveryPrice,
			roundingAdjustment,
			totalPrice: {
				// This is intentional. There is no VAT, so it does not matter.
				withoutTax: totalWithoutTax + roundingAdjustment,
				withTax: totalWithoutTax + roundingAdjustment,
			},
			appliedDiscounts: {
				deliveryPrice: deliveryDiscountWithoutTax,
				discountsByTaxRate,
			},
		};
	}

	const totalWithTax = roundToPlaces(withoutTax + tax, 2);
	const roundingAdjustment = calculateRoundingAdjustment(totalWithTax, order.payment.currency);

	return {
		deliveryPrice,
		roundingAdjustment,
		totalPrice: {
			withoutTax: roundToPlaces(withoutTax, 2),
			withTax: totalWithTax + roundingAdjustment,
		},
		appliedDiscounts: {
			deliveryPrice: deliveryDiscountWithTax,
			discountsByTaxRate,
		},
	};
}
